import { useRef } from 'react';
import type { Session } from '@/api/client';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ArrowForwardIcon, MenuIcon, ChatBubbleOutlineIcon } from '@/components/ui/Icons';
import { SessionCommentShell, type SessionCommentShellHandle } from '@/features/project-cell-comments/SessionCommentShell';

export type SessionPopoverCardMenuState = {
  scope: 'recorded' | 'planned' | 'montage' | 'otherPlanned';
  session: Session;
};

type Props = {
  session: Session;
  commentCount: number;
  commentTitle: string;
  dayNum: number;
  monthName: string;
  timeStart: string;
  timeEnd: string;
  studioLabel: string;
  engineerName: string;
  cardMenu: SessionPopoverCardMenuState | null;
  setCardMenu: React.Dispatch<React.SetStateAction<SessionPopoverCardMenuState | null>>;
  sessionButtonRefs: React.MutableRefObject<Record<string, HTMLElement | null>>;
  onEditSession?: (session: Session) => void;
  requestClose: () => void;
  goToSession: (args: { id: string; date: string }) => void;
};

export function ProjectSessionsModalMontageRow({
  session: s,
  commentCount,
  commentTitle,
  dayNum,
  monthName,
  timeStart,
  timeEnd,
  studioLabel,
  engineerName,
  cardMenu,
  setCardMenu,
  sessionButtonRefs,
  onEditSession,
  requestClose,
  goToSession,
}: Props) {
  const liRef = useRef<HTMLLIElement | null>(null);
  const shellRef = useRef<SessionCommentShellHandle | null>(null);

  return (
    <li
      ref={liRef}
      className="project-sessions-modal__card project-sessions-modal__card_montage project-sessions-modal__card_planned--comments-thread"
      onContextMenu={(e) => {
        e.preventDefault();
        shellRef.current?.openRmb(e);
      }}
    >
      <SessionCommentShell
        ref={shellRef}
        className="project-sessions-modal__session-comment-shell"
        sessionId={s.id}
        title={commentTitle}
        commentCount={commentCount}
      >
        <div className="project-sessions-modal__card-meta-row">
          <span className="project-sessions-modal__card-meta">
            <span className="project-sessions-modal__card-date project-sessions-modal__card-date_montage">
              <strong>{dayNum}</strong> {monthName}, с {timeStart} до {timeEnd}
            </span>
            <span className="project-sessions-modal__card-studio">{studioLabel}</span>
            <span className="project-sessions-modal__card-engineer">{engineerName}</span>
          </span>
          <div
            className="project-sessions-modal__card-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              ref={(el) => {
                sessionButtonRefs.current[s.id] = el ?? null;
              }}
              className="project-sessions-modal__card-menu-btn"
            >
              <IconButton
                aria-label="Меню"
                aria-haspopup="menu"
                aria-expanded={cardMenu?.session.id === s.id && cardMenu.scope === 'montage'}
                variant="default"
                size="m"
                iconSize={18}
                onClick={(e) => {
                  e.stopPropagation();
                  setCardMenu((m) =>
                    m?.session.id === s.id && m.scope === 'montage' ? null : { scope: 'montage', session: s }
                  );
                }}
                title="Меню"
              >
                <MenuIcon />
              </IconButton>
            </div>
            <ContextMenu
              isOpen={cardMenu?.session.id === s.id && cardMenu.scope === 'montage'}
              onClose={() => setCardMenu((prev) => (prev?.session.id === s.id ? null : prev))}
              anchorPosition={null}
              anchorEl={sessionButtonRefs.current[s.id]}
              alignY="center"
              anchorPriorityAxis="y"
              items={[
                {
                  label: 'Перейти к сессии',
                  icon: <ArrowForwardIcon size={18} />,
                  onClick: () => {
                    requestClose();
                    goToSession({ id: s.id, date: s.date ?? '' });
                  },
                },
                ...(onEditSession
                  ? [
                      {
                        label: 'Редактировать',
                        onClick: () => {
                          onEditSession(s);
                          requestClose();
                        },
                      },
                    ]
                  : []),
                {
                  label: commentCount > 0 ? 'Посмотреть комментарии' : 'Добавить комментарий',
                  icon: <ChatBubbleOutlineIcon size={18} />,
                  onClick: () => {
                    shellRef.current?.openCommentThread();
                    setCardMenu(null);
                  },
                },
              ]}
            />
          </div>
        </div>
      </SessionCommentShell>
    </li>
  );
}
